import { INITIAL_EPISODES, PodcastEpisode } from "@/data/podcasts";

export interface GuestRequest {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  instagram: string;
  facebook: string;
  tiktok: string;
  otherSocial: string;
  websiteLink: string;
  testimony: string;
  promoting: "yes" | "no" | "";
  promotionDetails: string;
  beenGuestBefore: "yes" | "no" | "";
  pastGuestLink: string;
  permissionToContact: boolean;
  acknowledgment: boolean;
  submittedAt: number;
  status: "pending" | "approved" | "rejected";
}

const EPISODES_KEY = "ieshia_podcast_episodes_v6";
const REQUESTS_KEY = "ieshia_podcast_guest_requests_v1";

// One-time cleanup of legacy storage keys from older builds so stale
// 7-episode caches can never shadow the current 30-episode seed.
const LEGACY_EPISODE_KEYS = [
  "ieshia_podcast_episodes",
  "ieshia_podcast_episodes_v1",
  "ieshia_podcast_episodes_v2",
  "ieshia_podcast_episodes_v3",
  "ieshia_podcast_episodes_v4",
  "ieshia_podcast_episodes_v5",
];

export const getStoredEpisodes = (): PodcastEpisode[] => {
  try {
    // Purge any legacy caches on load.
    LEGACY_EPISODE_KEYS.forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    });

    const saved = localStorage.getItem(EPISODES_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        // Always include the full seed list (30 episodes). Merge in any
        // user-added episodes (those whose ids are not part of the seed)
        // so manually added episodes are preserved on top of the seed.
        const initialIds = new Set(INITIAL_EPISODES.map((e) => e.id));
        const userAdded = parsed.filter((e) => !initialIds.has(e.id));
        return [...userAdded, ...INITIAL_EPISODES];
      }
    }
  } catch (e) {
    console.error("Failed to read episodes from localStorage", e);
  }
  return INITIAL_EPISODES;
};

export const saveStoredEpisodes = (episodes: PodcastEpisode[]) => {
  try {
    localStorage.setItem(EPISODES_KEY, JSON.stringify(episodes));
  } catch (e) {
    console.error("Failed to save episodes to localStorage", e);
  }
};

export const getStoredRequests = (): GuestRequest[] => {
  try {
    const saved = localStorage.getItem(REQUESTS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch (e) {
    console.error("Failed to read guest requests from localStorage", e);
  }
  return [];
};

export const saveStoredRequests = (requests: GuestRequest[]) => {
  try {
    localStorage.setItem(REQUESTS_KEY, JSON.stringify(requests));
  } catch (e) {
    console.error("Failed to save guest requests to localStorage", e);
  }
};

export const addGuestRequest = (
  req: Omit<GuestRequest, "id" | "submittedAt" | "status">,
): GuestRequest => {
  const newReq: GuestRequest = {
    ...req,
    id: `gr-${Date.now()}`,
    submittedAt: Date.now(),
    status: "pending",
  };
  const all = getStoredRequests();
  all.unshift(newReq);
  saveStoredRequests(all);
  return newReq;
};

// Migration: normalize legacy guest request records to the new schema
export const migrateGuestRequests = (): GuestRequest[] => {
  const all = getStoredRequests();
  const migrated = all.map((raw) => {
    // Legacy records may use firstName/lastName/topic; new schema uses fullName/testimony.
    const r = raw as Record<string, unknown> & GuestRequest;
    const legacyName =
      r.fullName ??
      `${(r as Record<string, unknown>).firstName ?? ""} ${(r as Record<string, unknown>).lastName ?? ""}`.trim();
    // Legacy records stored all social handles in a single "socialHandles" field.
    const legacySocial =
      (r.socialHandles as string) ??
      ((r as Record<string, unknown>).socialHandles as string) ??
      "";
    return {
      fullName: legacyName as string,
      email: r.email ?? "",
      phone: r.phone ?? "",
      instagram: r.instagram ?? legacySocial ?? "",
      facebook: r.facebook ?? "",
      tiktok: r.tiktok ?? "",
      otherSocial: r.otherSocial ?? "",
      websiteLink: r.websiteLink ?? "",
      testimony:
        r.testimony ?? ((r as Record<string, unknown>).topic as string) ?? "",
      promoting: r.promoting ?? "",
      promotionDetails: r.promotionDetails ?? "",
      beenGuestBefore: r.beenGuestBefore ?? "",
      pastGuestLink: r.pastGuestLink ?? "",
      permissionToContact: r.permissionToContact ?? false,
      acknowledgment: r.acknowledgment ?? false,
      id: r.id,
      submittedAt: r.submittedAt,
      status: r.status,
    } satisfies GuestRequest;
  });
  return migrated;
};
